import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageContainer, PageHeader, StatCard } from "@/components/Page";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API_BASE } from "@/lib/api";

const STATUS_COLORS = {
  pending: "#EAB308",
  ongoing: "#3B82F6",
  on_review: "#6366F1",
  complete: "#16A34A",
  failed: "#DC2626",
};

export default function SuperAdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports/summary");
        setSummary(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const statusData = summary ? Object.entries(summary.status_counts).map(([k, v]) => ({ name: k, value: v })) : [];
  const categoryData = summary ? Object.entries(summary.category_counts).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          eyebrow="Super Admin"
          title="Control Room"
          description="An overall outlook on estates, managers, and tickets across the network."
          actions={
            <a
              href={`${API_BASE}/reports/tickets.csv`}
              data-testid="download-csv-btn"
              className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-black hover:text-white hover:border-black transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Export Tickets CSV
            </a>
          }
        />

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <StatCard label="Estates" value={summary?.totals.estates ?? "—"} testid="stat-estates" />
          <StatCard label="Managers" value={summary?.totals.managers ?? "—"} testid="stat-managers" />
          <StatCard label="Residents" value={summary?.totals.residents ?? "—"} testid="stat-residents" />
          <StatCard label="Units" value={summary?.totals.units ?? "—"} testid="stat-units" />
          <StatCard label="Tickets" value={summary?.totals.tickets ?? "—"} testid="stat-tickets" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border border-zinc-200 p-6" data-testid="chart-status">
            <div className="label-eyebrow mb-4">Tickets by Status</div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={90} label>
                    {statusData.map((entry) => (
                      <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || "#71717A"} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 p-6" data-testid="chart-category">
            <div className="label-eyebrow mb-4">Tickets by Category</div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={categoryData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#000000" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between sticky left-0">
            <div className="label-eyebrow">Estate Breakdown</div>
            <Link to="/a/estates" className="text-xs font-semibold uppercase tracking-wider underline underline-offset-4" data-testid="manage-estates-link">Manage estates →</Link>
          </div>
          <table className="w-full text-sm min-w-[520px]">
            <thead className="border-b border-zinc-200">
              <tr className="text-left">
                <th className="p-4 label-eyebrow font-semibold">Estate</th>
                <th className="p-4 label-eyebrow font-semibold text-right">Total Tickets</th>
                <th className="p-4 label-eyebrow font-semibold text-right">Open</th>
              </tr>
            </thead>
            <tbody>
              {summary?.per_estate?.length ? summary.per_estate.map((e) => (
                <tr key={e.estate_id} className="border-b border-zinc-100" data-testid={`estate-row-${e.estate_id}`}>
                  <td className="p-4 font-medium">{e.name}</td>
                  <td className="p-4 text-right tabular-nums">{e.tickets}</td>
                  <td className="p-4 text-right tabular-nums font-bold">{e.open}</td>
                </tr>
              )) : (
                <tr><td colSpan={3} className="p-8 text-center text-zinc-500 text-sm">No estates yet. Create your first estate to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </PageContainer>
    </AppShell>
  );
}
